import {Image as ImageModel} from "@/model/image";

const collageLongSide = 1772
const collageShortSide = 1182
const outerPadding = 60
const innerPadding = 30

class ImageCollage {
    private static instance: ImageCollage
    private canvas: HTMLCanvasElement
    private readonly ctx:CanvasRenderingContext2D | null

    private constructor() {
        this.canvas = document.createElement('canvas')
        this.ctx = this.canvas.getContext("2d")
        this.resetCanvas()
    }

    private resetCanvas() {
        this.canvas.width = collageLongSide
        this.canvas.height = collageShortSide
        this.ctx!.fillStyle = '#ffffff'
        this.ctx?.fillRect(0,0, this.canvas.width, this.canvas.height)
    }

    public static getInstance() : ImageCollage {
        if (!ImageCollage.instance) {
            ImageCollage.instance = new ImageCollage()
        }
        return ImageCollage.instance
    }

    public create(images: ImageModel[]): Promise<{ uri: string, orientation: string }>{
        return new Promise<{ uri: string, orientation: string }>(((resolve,reject) => {
            if (images.length <= 0) {
                reject('no images selected')
                return
            }
            this.resetCanvas()
            const type = this.collageType(images)
            let fn:Promise<void>
            let orientation = 'portrait'
            switch (type) {
                case 'P':
                    fn = this.createP(images)
                    break
                case 'PP':
                    fn = this.createPP(images)
                    orientation = 'landscape'
                    break
                case 'PPP':
                    fn = this.createPPP(images)
                    orientation = 'landscape'
                    break
                case 'PL':
                case 'PLL':
                    fn = this.createPLorPLL(images)
                    orientation = 'landscape'
                    break
                case 'PPL':
                    fn = this.createPPL(images)
                    break
                case 'L':
                    fn = this.createL(images)
                    orientation = 'landscape'
                    break
                case 'LL':
                    fn = this.createLL(images)
                    break
                case 'LLL':
                    fn = this.createLLL(images)
                    break
                default:
                    reject(`'${type}' is no valid selection of images`)
                    return
            }
            fn.then(() => resolve({uri: this.canvas.toDataURL("image/jpeg", 0.5), orientation: orientation}))
        }))
    }

    private drawImageRotated(img: CanvasImageSource, dx: number, dy: number, width: number, height: number, degrees: number) {
        this.ctx?.save()
        this.ctx?.translate(this.canvas.width, 0)
        this.ctx?.rotate(degrees*Math.PI/180)
        this.ctx?.drawImage(img, dx, dy, width, height)
        this.ctx?.restore()
    }

    private createP(images: ImageModel[]):Promise<void> {
        return new Promise<void>((resolve) => {
            this.loadImage(images[0].href).then(img => {
                const width = collageShortSide-2*outerPadding
                const height = width/images[0].ratio
                this.drawImageRotated(img, outerPadding, outerPadding, width, height, 90)
                resolve()
            })
        })
    }

    private createL(images: ImageModel[]):Promise<void> {
        return new Promise<void>((resolve) => {
            this.loadImage(images[0].href).then(img => {
                const height = collageShortSide-outerPadding*2
                const width = height*images[0].ratio
                this.ctx?.drawImage(img, outerPadding, outerPadding, width, height)
                resolve()
            })
        })
    }

    private createPP(images: ImageModel[]):Promise<void> {
        return new Promise<void>((resolve) => {
            const imgLeft = images[0]
            const imgRight = images[1]
            Promise.all([this.loadImage(imgLeft.href), this.loadImage(imgRight.href)])
                .then(([lImg, rImg]) => {
                    const w1 = (collageLongSide-outerPadding*2 - innerPadding)/2
                    const h1 = w1/images[0].ratio
                    const h2 = w1/images[1].ratio
                    this.ctx?.drawImage(lImg, outerPadding, outerPadding, w1, h1)
                    this.ctx?.drawImage(rImg, outerPadding+w1+innerPadding, outerPadding, w1, h2)
                    resolve()
            })
        })
    }

    private createPLorPLL(images: ImageModel[]):Promise<void> {
        return new Promise<void>((resolve) => {
            const loads:Promise<HTMLImageElement>[] = []
            const portrait = images.find(i => i.orientation === 'portrait')
            loads.push(this.loadImage(portrait!.href))
            const landscapes = images.filter(i => i.orientation === 'landscape')
            landscapes.forEach(l => loads.push(this.loadImage(l.href)))

            Promise.all(loads)
                .then((imgs) => {
                    const w1 = (collageLongSide-outerPadding*2 - innerPadding)/2
                    const h1 = w1/portrait!.ratio
                    const h2 = (h1-innerPadding)/2
                    const w2 = h2*landscapes[0].ratio
                    this.ctx?.drawImage(imgs[0], outerPadding, outerPadding, w1, h1)
                    this.ctx?.drawImage(imgs[1], outerPadding+w1+innerPadding, outerPadding, w2, h2)
                    if (landscapes.length == 2) {
                        const w3 = h2*landscapes[1].ratio
                        this.ctx?.drawImage(imgs[2], outerPadding+w1+innerPadding, outerPadding+innerPadding+h2, w3, h2)
                    }
                    resolve()
                })
        })
    }

    private createPPL(images: ImageModel[]):Promise<void> {
        return new Promise<void>((resolve) => {
            const loads:Promise<HTMLImageElement>[] = []
            const landscape = images.find(i => i.orientation === 'landscape')
            loads.push(this.loadImage(landscape!.href))
            const portraits = images.filter(i => i.orientation === 'portrait')
            portraits.forEach(l => loads.push(this.loadImage(l.href)))

            Promise.all(loads)
                .then((imgs) => {
                    const w1 = collageShortSide-2*outerPadding
                    const h1 = w1/landscape!.ratio
                    const w2 = (w1-innerPadding)/2
                    const h2 = w2/portraits[0].ratio
                    this.drawImageRotated(imgs[0], outerPadding, outerPadding, w1, h1, 90)
                    this.drawImageRotated(imgs[1], outerPadding, outerPadding+h1+innerPadding, w2, h2, 90)
                    if (portraits.length == 2) {
                        const h3 = w2/portraits[1].ratio
                        this.drawImageRotated(imgs[2], outerPadding+innerPadding+w2, outerPadding+h1+innerPadding, w2, h3,90)
                    }
                    resolve()
                })
        })
    }

    private createPPP(images: ImageModel[]):Promise<void> {
        return new Promise<void>((resolve) => {
            const imgLeft = images[0]
            const imgCenter = images[1]
            const imgRight = images[2]
            Promise.all([this.loadImage(imgLeft.href), this.loadImage(imgCenter.href), this.loadImage(imgRight.href)])
                .then(([lImg, cImg, rImg]) => {
                    const w1 = (collageLongSide-outerPadding*2 - innerPadding*2)/3
                    const h1 = w1/imgLeft.ratio
                    const h2 = w1/imgCenter.ratio
                    const h3 = w1/imgRight.ratio
                    this.ctx?.drawImage(lImg, outerPadding, outerPadding, w1, h1)
                    this.ctx?.drawImage(cImg, outerPadding+innerPadding+w1, outerPadding, w1, h2)
                    this.ctx?.drawImage(rImg, outerPadding+(innerPadding+w1)*2, outerPadding, w1, h3)
                    resolve()
                })
        })
    }

    private createLL(images: ImageModel[]):Promise<void> {
        return new Promise<void>((resolve) => {
            const imgUpper = images[0]
            const imgLower = images[1]
            Promise.all([this.loadImage(imgUpper.href), this.loadImage(imgLower.href)])
                .then(([uImg, lImg]) => {
                    const w1 = collageShortSide-outerPadding*2-innerPadding
                    const h1 = w1/imgUpper.ratio
                    const h2 = w1/imgLower.ratio
                    this.drawImageRotated(uImg, outerPadding, outerPadding, w1, h1, 90)
                    this.drawImageRotated(lImg, outerPadding, outerPadding+innerPadding+h1, w1, h2, 90)
                    resolve()
                })
        })
    }

    private createLLL(images: ImageModel[]):Promise<void> {
        return new Promise<void>((resolve) => {
            const imgUpper = images[0]
            const imgMiddle = images[1]
            const imgLower = images[2]
            Promise.all([this.loadImage(imgUpper.href), this.loadImage(imgMiddle.href), this.loadImage(imgLower.href)])
                .then(([uImg, mImg, lImg]) => {
                    const h1 = (collageLongSide-outerPadding*2 - innerPadding-innerPadding)/3
                    const w1 = h1*images[0].ratio
                    const w2 = h1*images[1].ratio
                    const w3 = h1*images[2].ratio
                    this.drawImageRotated(uImg, outerPadding, outerPadding, w1, h1, 90)
                    this.drawImageRotated(mImg, outerPadding, outerPadding+innerPadding+h1, w2, h1, 90)
                    this.drawImageRotated(lImg, outerPadding, outerPadding+(innerPadding+h1)*2, w3, h1, 90)
                    resolve()
                })
        })
    }

    private loadImage(uri: string): Promise<HTMLImageElement> {
        const img = new Image()
        img.crossOrigin="anonymous"
        return new Promise<HTMLImageElement>((resolve, reject) => {
            img.onload = (e: Event) => {
                resolve(e.target as HTMLImageElement)
            }
            img.src = uri
        })
    }

    private collageType(images: ImageModel[]): string {
        let type = ""
        images.forEach(i => {
            if (i.orientation === "portrait") {
                type = "P"+type
            } else {
                type = type + "L"
            }
        })
        return type
    }
}

export const useImageCollage = () => {
    return ImageCollage.getInstance()
}